import { lazy, Suspense, useCallback, useMemo, memo } from "react";
import { BsPersonBadge } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import "../../components/css/Investing.css";
import { useAppContext } from "../../Context/AppContext";
import { useGetInvestorDetails } from "../../api/queries/MuslimInvest/useGetInvestorDetails";
import { useGetCompanyDetails } from "../../api/queries/MuslimInvest/useGetCompanyDetails";
import { useTranslation } from "react-i18next";

const Pitches = lazy(() => import("./Pitches"));
const CompanyStatus = lazy(() => import("./CompanyProfile/CompanyStatus"));
const CompanyCheckout = lazy(() => import("./CompanyProfile/CompanyCheckout"));
const Pitch = lazy(() => import("../pitch/Pitch"));
const RejectedProfile = lazy(() => import("./RejectedProfile"));
const InvestorCheckout = lazy(
  () => import("./InvestorProfile/InvestorCheckout"),
);
const CompanyPitchDisplay = lazy(() => import("./CompanyPitchDisplay"));

const LOADING_FALLBACK_STYLE = { minHeight: 180 };

const RegisterProfile = memo(function RegisterProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: userData } = useAppContext();

  const { data: investorData, isPending: investorLoading } =
    useGetInvestorDetails({
      userId: userData?.user.id.toString() || "",
    });
  const { data: companyData, isPending: companyLoading } = useGetCompanyDetails(
    {
      userId: userData?.user.id.toString() || "",
    },
  );

  const profile = useMemo(
    () => investorData?.[0] ?? companyData?.[0] ?? null,
    [investorData, companyData],
  );

  const handleNavigateInvestor = useCallback(
    () => navigate("investor-profile"),
    [navigate],
  );
  const handleNavigateCompany = useCallback(
    () => navigate("company-profile"),
    [navigate],
  );

  if (investorLoading || companyLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="-mx-2 pb-4">
      {profile?.status === "Registered" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <Pitches />
        </Suspense>
      ) : profile?.status === "Awaiting Approval" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <CompanyStatus />
        </Suspense>
      ) : profile?.status === "Accepted" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <CompanyCheckout />
        </Suspense>
      ) : profile?.status === "Payment Pending" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <InvestorCheckout />
        </Suspense>
      ) : profile?.status === "Rejected" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <RejectedProfile />
        </Suspense>
      ) : profile?.status === "Live" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <CompanyPitchDisplay />
        </Suspense>
      ) : profile?.status === "pitchInProgress" ? (
        <Suspense
          fallback={<div style={LOADING_FALLBACK_STYLE} aria-hidden="true" />}
        >
          <Pitch />
        </Suspense>
      ) : (
        <div className="container-fluid px-0 mb-3">
          <h1 className="text-3xl fw-bold">
            {t("muslimInvest.registerProfile")}
          </h1>

          <div className="d-flex flex-column flex-md-row gap-3 gap-md-4 mt-3 mt-md-4">
            <div
              className="cursor-pointer w-100 rounded-4 shadow overflow-hidden create-card"
              onClick={handleNavigateInvestor}
            >
              <div className="p-4">
                <BsPersonBadge size={40} />
              </div>
              <div className="bg-white p-4">
                <h3 className="text-xl mb-2">
                  {t("muslimInvest.createInvestorProfile")}
                </h3>
                <p className="text-gray-600">
                  {t("muslimInvest.buildInvestorProfile")}
                </p>
              </div>
            </div>
            <div
              className="cursor-pointer w-100 rounded-4 shadow overflow-hidden create-card"
              onClick={handleNavigateCompany}
            >
              <div className="p-4">
                <BsPersonBadge size={40} />
              </div>
              <div className="bg-white p-4">
                <h3 className="text-xl mb-2">
                  {t("muslimInvest.createCompanyProfileSeeking")}
                </h3>
                <p className="text-gray-600">
                  {t("muslimInvest.buildCompanyProfile")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

RegisterProfile.displayName = "RegisterProfile";

export default RegisterProfile;
